import React, { useState, useEffect } from 'react';
import axios from 'axios';

const RefundDetailsModal = ({ isOpen, onClose, dealId }) => {
  const [loading, setLoading] = useState(true);
  const [refunds, setRefunds] = useState([]);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (isOpen && dealId) {
      fetchRefundDetails();
    }
  }, [isOpen, dealId]);
  
  const fetchRefundDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const response = await axios.get(`/api/refunds/deal/${dealId}`, config);
      setRefunds(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching refund details:', err);
      setError('Failed to load refund details: ' + (err.response?.data?.message || err.message));
      setLoading(false);
    }
  };
  
  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  
  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  // Format the currency for display
  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">Refund History</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-10 h-10 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin mx-auto mb-2"></div>
              <p>Loading refund details...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <p>{error}</p>
            </div>
          ) : refunds.length === 0 ? (
            <div className="text-center py-8 text-gray-600">
              <p>No refund records found for this deal.</p>
            </div>
          ) : (
            <div>
              {refunds.map((refund) => (
                <div key={refund.id} className="mb-6 border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 p-3 border-b">
                    <div className="flex justify-between items-center">
                      <h3 className="font-medium">Refund #{refund.id}</h3>
                      <span className="text-sm text-gray-600">{formatDate(refund.created_at)}</span>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-semibold mb-1">Amount</p>
                        <p className="text-xl font-bold text-red-600">
                          {formatCurrency(refund.amount, 'USD')}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-semibold mb-1">Reason</p>
                        <p>{refund.reason || '-'}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-semibold mb-1">Tutor</p>
                        <p>{refund.tutor_name || '-'}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-semibold mb-1">Lessons Completed</p>
                        <p>{refund.lessons_completed !== null ? refund.lessons_completed : '-'}</p>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-sm font-semibold mb-1">Processed By</p>
                      <p>{refund.processor_name || '-'}</p>
                    </div>
                    
                    {refund.reason_details && (
                      <div>
                        <p className="text-sm font-semibold mb-1">Additional Details</p>
                        <p className="whitespace-pre-wrap bg-gray-50 p-3 rounded border text-sm">
                          {refund.reason_details}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t flex justify-end sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md shadow-sm hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundDetailsModal;